import { z } from 'zod';
import type {
    GetMyLegalAgreementResponse,
    MyLegalAgreement,
    MyLegalAgreementsSearchRequest,
    SearchMyLegalAgreementsPageResponse,
} from '../my-legal-agreements.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { NonEmptyStringSchema } from './common.schemas';
import { SystemDisplayTimeZoneSchema } from '../timezone.utils';
import { LegalDocumentsOpaqueCursorQuerySchema } from './legal-documents.schemas';

export const MyLegalAgreementsSearchRequestSchema: z.ZodType<MyLegalAgreementsSearchRequest> = z
    .object({
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: LegalDocumentsOpaqueCursorQuerySchema,
    })
    .strict();

const myLegalAgreementOrganizationSchema = z
    .object({ organization_uuid: z.string().uuid(), organization_name: NonEmptyStringSchema })
    .strict();

const myLegalAgreementBaseSchema = z.object({
    acceptance_uuid: z.string().uuid(),
    accepted_ts: z.string().datetime(),
    legal_document_title: NonEmptyStringSchema,
    legal_document_version: NonEmptyStringSchema,
    accepted_text_matches_current: z.boolean(),
    withdrawn_ts: z.string().datetime().nullable(),
});

export const MyLegalAgreementSchema: z.ZodType<MyLegalAgreement> = z.discriminatedUnion(
    'acceptance_kind',
    [
        myLegalAgreementBaseSchema
            .extend({
                acceptance_kind: z.literal('user'),
                organization: myLegalAgreementOrganizationSchema.nullable(),
            })
            .strict(),
        myLegalAgreementBaseSchema
            .extend({
                acceptance_kind: z.literal('organization'),
                organization: myLegalAgreementOrganizationSchema,
                signer_authority_confirmed: z.boolean(),
            })
            .strict(),
    ]
);

export const SearchMyLegalAgreementsPageResponseSchema: z.ZodType<SearchMyLegalAgreementsPageResponse> =
    z
        .object({
            my_legal_agreements: z.array(MyLegalAgreementSchema),
            pagination: OpaqueCursorPaginationSchema,
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();

export const GetMyLegalAgreementResponseSchema: z.ZodType<GetMyLegalAgreementResponse> = z
    .object({
        my_legal_agreement: MyLegalAgreementSchema,
        accepted_content: z
            .object({
                legal_document_text: z.string(),
                legal_document_content_type: NonEmptyStringSchema,
            })
            .strict(),
    })
    .strict();
